import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { eventsAPI, favoritesAPI, applicationsAPI, eventSeriesAPI, tasksAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { EVENT_TYPES, EVENT_ICONS, formatDate, formatEventDates, formatCurrency, getDaysUntil } from '../lib/utils';
import { toast } from 'sonner';
import { SeriesHistoryPanel } from '../components/SeriesHistoryPanel';
import { EventRating } from '../components/EventRating';
import { EventCommunityFooter } from '../components/EventCommunityFooter';
import {
  MapPin,
  Calendar,
  DollarSign,
  Users,
  Globe,
  Mail,
  Heart,
  ArrowLeft,
  ExternalLink,
  Clock,
  Loader2,
  Edit,
  Trash2,
  Phone,
  User,
  FileText,
  CheckCircle2,
  Send,
  XCircle,
  Eye,
  ListTodo,
  Building,
  TreePine,
  UsersRound,
  Star,
  History,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Link2,
  Package,
  ChevronRight,
  AlertCircle,
  CircleDot,
  Circle,
  CheckCircle,
  StickyNote,
  CalendarClock,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

// Application status config
const APPLICATION_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileText },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
  waitlisted: { label: 'Waitlisted', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Clock },
};

export const EventDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favoriting, setFavoriting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [application, setApplication] = useState(null);
  const [applicationTasks, setApplicationTasks] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [seriesHistory, setSeriesHistory] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);

  const loadEvent = async () => {
    try {
      const res = await eventsAPI.getOne(id);
      setEvent(res.data);
      
      // Fetch application if user is logged in
      if (user) {
        try {
          const appRes = await applicationsAPI.getByEvent(id);
          setApplication(appRes.data);
          setApplicationTasks(appRes.data.tasks || []);
        } catch (error) {
          // No application exists
          setApplication(null);
          setApplicationTasks([]);
        }
      }
    } catch (error) {
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const loadSeriesHistory = async () => {
    if (!user) return;
    setSeriesLoading(true);
    try {
      const historyRes = await eventsAPI.getSeriesHistory(id);
      if (historyRes.data.has_series) {
        setSeriesHistory(historyRes.data.series);
      } else {
        setSeriesHistory(null);
      }
    } catch (error) {
      console.error('Failed to load series history:', error);
    } finally {
      setSeriesLoading(false);
    }
  };

  useEffect(() => {
    loadEvent();
  }, [id, navigate, user]);

  useEffect(() => {
    if (user && event) {
      loadSeriesHistory();
    }
  }, [user, event?.id]);

  const handleFavorite = async () => {
    if (!user) {
      toast.error('Please log in to save events');
      return;
    }

    setFavoriting(true);
    try {
      if (event.is_favorited) {
        await favoritesAPI.remove(event.id);
        setEvent({ ...event, is_favorited: false });
        toast.success('Removed from favorites');
      } else {
        await favoritesAPI.add(event.id);
        setEvent({ ...event, is_favorited: true });
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    } finally {
      setFavoriting(false);
    }
  };

  const handleApply = async () => {
    if (!user) {
      toast.error('Please log in to apply to events');
      navigate('/login');
      return;
    }

    setIsApplying(true);
    try {
      // First, ensure the event is saved
      if (!event.is_favorited) {
        await favoritesAPI.add(event.id);
        setEvent({ ...event, is_favorited: true });
      }
      
      // Create the application
      const response = await applicationsAPI.create({
        event_id: event.id,
        profile_id: null,
        task_template_id: 'template_craft_show',
        notes: null,
      });
      
      setApplication(response.data);
      toast.success('Application created! You can now manage your tasks.');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already exists')) {
        // Fetch the existing application
        try {
          const appRes = await applicationsAPI.getByEvent(id);
          setApplication(appRes.data);
          toast.info('You already have an application for this event');
        } catch {
          navigate('/applications');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create application');
      }
    } finally {
      setIsApplying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await eventsAPI.delete(event.id);
      toast.success('Event deleted');
      navigate('/events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const appStatusConfig = application ? APPLICATION_STATUS[application.status] : null;
  const StatusIcon = appStatusConfig?.icon;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
      </div>
    );
  }

  if (!event) return null;

  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
  const daysUntilEvent = getDaysUntil(event.date);
  const daysUntilDeadline = getDaysUntil(event.deadline);
  // User can edit if they created the event OR if they have saved it
  const isOwner = user && event.created_by === user.id;
  // Community events are admin-only for editing — regular users don't see Edit.
  // Otherwise: the creator can edit, or (legacy) a favoriter can edit their saved copy.
  const canEdit = user && (
    user.is_admin ||
    (!event.is_community && (event.created_by === user.id || event.is_favorited))
  );

  // Get the best thumbnail for the event header
  // Priority: 1) image_url (user uploaded) 2) thumbnail_url (scraped from website) 3) gray placeholder
  const getEventHeaderImage = () => {
    if (event.image_url) {
      return { type: 'image', url: event.image_url };
    }
    if (event.thumbnail_url) {
      return { type: 'image', url: event.thumbnail_url };
    }
    return { type: 'placeholder', url: null };
  };

  const headerImage = getEventHeaderImage();
  const EventTypeIcon = eventType.icon && EVENT_ICONS[eventType.icon] ? EVENT_ICONS[eventType.icon] : Calendar;

  return (
    <div className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        {/* Back button - returns to last used view */}
        <Link
          to="/events"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
          data-testid="back-to-events"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to My Events
        </Link>

        {/* Hero Image */}
        <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden border-2 border-[#1E3A5F] shadow-hard mb-8">
          {headerImage.type === 'image' ? (
            <img
              src={headerImage.url}
              alt={event.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                // On error, hide image and show placeholder
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Gray Placeholder (shown when no image or on image error) */}
          <div 
            className={`absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center ${headerImage.type === 'image' ? 'hidden' : 'flex'}`}
          >
            <div className="text-center">
              <EventTypeIcon className="w-24 h-24 text-gray-400 mx-auto mb-3" />
              <span className="text-lg text-gray-500 font-medium">{eventType.label}</span>
            </div>
          </div>
          
          {/* Category Badge */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <div className={`badge-category badge-${event.event_type}`}>
              {eventType.label}
            </div>
            {event.is_community && (
              <div className="bg-[#1E3A5F] text-white font-bold px-3 py-1.5 rounded-full border-2 border-white flex items-center gap-1.5 text-sm shadow-md" data-testid="community-event-badge">
                <UsersRound className="w-4 h-4" />
                Community
              </div>
            )}
          </div>

          {/* Deadline Badge */}
          {daysUntilDeadline !== null && daysUntilDeadline >= 0 && daysUntilDeadline <= 14 && (
            <div className="absolute top-4 right-4 bg-[#FE5F55] text-white font-bold px-4 py-2 rounded-full border-2 border-[#1E3A5F]">
              {daysUntilDeadline === 0 ? 'Deadline Today!' : `${daysUntilDeadline} days to apply`}
            </div>
          )}
        </div>

        {/* Title and Quick Info */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#1E3A5F] mb-4">
            {event.title}
          </h1>
          
          <div className="flex flex-wrap gap-4 text-[#4A4A4A]">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#FE5F55]" />
              <span>{event.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#FE5F55]" />
              <span className="font-medium">
                {formatDate(event.date)}
                {event.end_date && event.end_date !== event.date && ` - ${formatDate(event.end_date)}`}
              </span>
            </div>
            {seriesHistory && (
              <div className="flex items-center gap-2 bg-[#1E3A5F]/10 px-3 py-1 rounded-full">
                <History className="w-4 h-4 text-[#1E3A5F]" />
                <span className="text-[#1E3A5F] font-medium text-sm">Part of {seriesHistory.name} series</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 p-1 rounded-xl h-12">
            <TabsTrigger 
              value="details" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E3A5F] data-[state=active]:shadow-md font-semibold flex items-center gap-2"
              data-testid="tab-details"
            >
              <FileText className="w-4 h-4" />
              Event Details
            </TabsTrigger>
            <TabsTrigger 
              value="series" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E3A5F] data-[state=active]:shadow-md font-semibold flex items-center gap-2"
              data-testid="tab-series"
            >
              <History className="w-4 h-4" />
              Series History
              {seriesHistory && (
                <span className="bg-[#1E3A5F] text-white text-xs px-2 py-0.5 rounded-full">
                  {seriesHistory.years_attended}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Event Details Tab */}
          <TabsContent value="details">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {event.description && (
                  <div className="card-dashboard">
                    <h2 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-3">About This Event</h2>
                    <p className="text-[#4A4A4A] leading-relaxed whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}

                {/* Additional date ranges */}
                {event.event_dates && event.event_dates.length > 0 && (
                  <div className="card-dashboard">
                    <h2 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-3">Additional Dates</h2>
                    <div className="space-y-2">
                      {event.event_dates.map((dateRange, index) => (
                        <div key={index} className="flex items-center gap-2 text-[#4A4A4A]">
                          <Calendar className="w-4 h-4 text-[#FE5F55]" />
                          <span>
                            {formatDate(dateRange.start_date)}
                            {dateRange.end_date && dateRange.end_date !== dateRange.start_date && ` - ${formatDate(dateRange.end_date)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {(event.website_url || event.contact_email || event.contact_name || event.contact_phone) && (
                  <div className="card-dashboard">
                    <h2 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-4">Contact Information</h2>
                    <div className="space-y-3">
                      {event.contact_name && (
                        <div className="flex items-center gap-3 text-[#4A4A4A]" data-testid="event-contact-name">
                          <User className="w-5 h-5 text-[#1E3A5F]" />
                          <span className="font-medium">{event.contact_name}</span>
                        </div>
                      )}
                      {event.contact_phone && (
                        <a
                          href={`tel:${event.contact_phone}`}
                          className="flex items-center gap-3 text-[#1E3A5F] hover:underline font-medium"
                          data-testid="event-contact-phone"
                        >
                          <Phone className="w-5 h-5" />
                          {event.contact_phone}
                        </a>
                      )}
                      {event.contact_email && (
                        <a
                          href={`mailto:${event.contact_email}`}
                          className="flex items-center gap-3 text-[#1E3A5F] hover:underline font-medium"
                          data-testid="event-email-link"
                        >
                          <Mail className="w-5 h-5" />
                          {event.contact_email}
                        </a>
                      )}
                      {event.website_url && (
                        <a
                          href={event.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 text-[#1E3A5F] hover:underline font-medium"
                          data-testid="event-website-link"
                        >
                          <Globe className="w-5 h-5" />
                          Visit Website
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      {event.signup_url && (
                        <a
                          href={event.signup_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 text-[#1E3A5F] hover:underline font-medium"
                          data-testid="event-signup-link"
                        >
                          <FileText className="w-5 h-5" />
                          Apply/Sign Up
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Info Card */}
                <div className="card-dashboard">
                  <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-4">Event Details</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                      <div className="flex items-center gap-2 text-[#4A4A4A]">
                        <DollarSign className="w-5 h-5 text-[#1E3A5F]" />
                        <span>Booth Cost</span>
                      </div>
                      <span className="font-bold text-[#1E3A5F]">{formatCurrency(event.booth_cost)}</span>
                    </div>

                    {event.event_size && (
                      <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                        <div className="flex items-center gap-2 text-[#4A4A4A]">
                          <Users className="w-5 h-5 text-[#1E3A5F]" />
                          <span>Event Size</span>
                        </div>
                        <span className="font-bold text-[#1E3A5F] capitalize">{event.event_size}</span>
                      </div>
                    )}

                    {event.venue_type && (
                      <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                        <div className="flex items-center gap-2 text-[#4A4A4A]">
                          {event.venue_type === 'indoor' ? (
                            <Building className="w-5 h-5 text-[#1E3A5F]" />
                          ) : event.venue_type === 'outdoor' ? (
                            <TreePine className="w-5 h-5 text-green-600" />
                          ) : (
                            <Building className="w-5 h-5 text-[#1E3A5F]" />
                          )}
                          <span>Venue Type</span>
                        </div>
                        <span className="font-bold text-[#1E3A5F] capitalize">{event.venue_type}</span>
                      </div>
                    )}

                    {(event.start_time || event.end_time || event.event_hours) && (
                      <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                        <div className="flex items-center gap-2 text-[#4A4A4A]">
                          <Clock className="w-5 h-5 text-[#1E3A5F]" />
                          <span>Hours</span>
                        </div>
                        <span className="font-bold text-[#1E3A5F] text-right text-sm">
                          {(() => {
                            const fmt = (hhmm) => {
                              if (!hhmm || typeof hhmm !== 'string') return null;
                              const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
                              if (!m) return null;
                              const [, h, mm] = m;
                              const hour = parseInt(h, 10);
                              if (isNaN(hour) || hour < 0 || hour > 23) return null;
                              const period = hour >= 12 ? 'PM' : 'AM';
                              const hour12 = ((hour + 11) % 12) + 1;
                              return `${hour12}:${mm} ${period}`;
                            };
                            const s = fmt(event.start_time);
                            const e = fmt(event.end_time);
                            if (s && e) return `${s} – ${e}`;
                            if (s) return `From ${s}`;
                            if (e) return `Until ${e}`;
                            return event.event_hours;
                          })()}
                        </span>
                      </div>
                    )}

                    {event.deadline && (
                      <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                        <div className="flex items-center gap-2 text-[#4A4A4A]">
                          <Clock className="w-5 h-5 text-[#FE5F55]" />
                          <span>Deadline</span>
                        </div>
                        <span className="font-bold text-[#1E3A5F]">{formatDate(event.deadline)}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2 text-[#4A4A4A]">
                        <MapPin className="w-5 h-5 text-[#FE5F55]" />
                        <span>Location</span>
                      </div>
                      <span className="font-bold text-[#1E3A5F]">{event.city}, {event.state}</span>
                    </div>
                  </div>
                </div>

                {/* Application Status Card */}
                {user && (
                  <div className="card-dashboard" data-testid="application-status-card">
                    <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#1E3A5F]" />
                      Application
                    </h2>
                    
                    {application ? (
                      <div className="space-y-4">
                        {/* Status Badge */}
                        <div className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 ${appStatusConfig?.color || 'bg-gray-100 text-gray-700 border-gray-300'}`} data-testid="app-status-badge">
                          {StatusIcon && <StatusIcon className="w-5 h-5" />}
                          <span className="font-bold text-lg">{appStatusConfig?.label || application.status}</span>
                        </div>
                        
                        {/* Key Dates */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <CalendarClock className="w-3.5 h-3.5" />
                              <span>Created</span>
                            </div>
                            <span className="font-medium text-[#1E3A5F]">{formatDate(application.created_at?.split('T')[0])}</span>
                          </div>
                          {application.submission_date && (
                            <div className="flex items-center justify-between text-gray-600">
                              <div className="flex items-center gap-1.5">
                                <Send className="w-3.5 h-3.5" />
                                <span>Submitted</span>
                              </div>
                              <span className="font-medium text-[#1E3A5F]">{formatDate(application.submission_date?.split('T')[0])}</span>
                            </div>
                          )}
                          {application.response_date && (
                            <div className="flex items-center justify-between text-gray-600">
                              <div className="flex items-center gap-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span>Response</span>
                              </div>
                              <span className="font-medium text-[#1E3A5F]">{formatDate(application.response_date?.split('T')[0])}</span>
                            </div>
                          )}
                          {application.coordinator_email && (
                            <div className="flex items-center justify-between text-gray-600">
                              <div className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" />
                                <span>Coordinator</span>
                              </div>
                              <a href={`mailto:${application.coordinator_email}`} className="font-medium text-[#1E3A5F] hover:underline truncate max-w-[140px]">
                                {application.coordinator_email}
                              </a>
                            </div>
                          )}
                        </div>
                        
                        {/* Notes */}
                        {application.notes && (
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid="app-notes">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                              <StickyNote className="w-3 h-3" />
                              Notes
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-4">{application.notes}</p>
                          </div>
                        )}

                        {/* Task Progress */}
                        {applicationTasks.length > 0 && (
                          <div data-testid="app-task-progress">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                <ListTodo className="w-3 h-3" />
                                Tasks
                              </span>
                              <span className="text-xs font-bold text-[#1E3A5F]">
                                {applicationTasks.filter(t => t.completed).length}/{applicationTasks.length}
                              </span>
                            </div>
                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                              <div
                                className="bg-emerald-500 h-2 rounded-full transition-all"
                                style={{ width: `${applicationTasks.length > 0 ? (applicationTasks.filter(t => t.completed).length / applicationTasks.length) * 100 : 0}%` }}
                              />
                            </div>
                            {/* Task List (show up to 5) */}
                            <div className="space-y-1.5">
                              {applicationTasks.slice(0, 5).map(task => (
                                <div key={task.id} className="flex items-start gap-2 text-sm" data-testid={`app-task-${task.id}`}>
                                  {task.completed ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                                  )}
                                  <span className={`${task.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                    {task.title}
                                  </span>
                                </div>
                              ))}
                              {applicationTasks.length > 5 && (
                                <p className="text-xs text-gray-500 pl-6">+{applicationTasks.length - 5} more tasks</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Profile Info */}
                        {application.profile && (
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200" data-testid="app-profile-info">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
                              <User className="w-3 h-3" />
                              Profile: {application.profile.name}
                            </div>
                            {application.profile.business_name && (
                              <p className="text-sm text-blue-700 font-medium">{application.profile.business_name}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Action Button */}
                        <Link to="/applications">
                          <Button className="w-full bg-[#1E3A5F] hover:bg-[#15325F] text-white" data-testid="view-application-btn">
                            <Eye className="w-4 h-4 mr-2" />
                            View Full Application
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Start your application to track tasks and stay organized for this event.
                        </p>
                        <Button
                          onClick={handleApply}
                          disabled={isApplying}
                          className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white font-semibold"
                          data-testid="start-application-btn"
                        >
                          {isApplying ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Creating Application...
                            </>
                          ) : (
                            <>
                              <ListTodo className="w-4 h-4 mr-2" />
                              Start Application
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={handleFavorite}
                    disabled={favoriting}
                    className={`w-full ${event.is_favorited ? 'btn-accent' : 'btn-secondary'}`}
                    data-testid="event-favorite-btn"
                  >
                    {favoriting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Heart className={`w-5 h-5 mr-2 ${event.is_favorited ? 'fill-current' : ''}`} />
                        {event.is_favorited ? 'Saved' : 'Save Event'}
                      </>
                    )}
                  </Button>

                  {event.website_url && (
                    <a href={event.website_url} target="_blank" rel="noopener noreferrer" className="block">
                      <Button className="btn-primary w-full" data-testid="event-external-apply-btn">
                        Visit Event Website
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  )}

                  {canEdit && (
                    <div className="flex gap-3 pt-4 border-t border-[#E5E5E5]">
                      <Link to={`/events/${event.id}/edit`} className="flex-1">
                        <Button variant="outline" className="w-full border-2 border-[#1E3A5F] text-[#1E3A5F]" data-testid="event-edit-btn">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </Link>
                      {isOwner && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="border-2 border-red-500 text-red-500 hover:bg-red-50" data-testid="event-delete-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="border-2 border-[#1E3A5F] rounded-xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Event?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will permanently delete the event.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-2 border-[#1E3A5F]">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDelete}
                              disabled={deleting}
                              className="bg-red-500 hover:bg-red-600 text-white"
                            >
                              {deleting ? 'Deleting...' : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Community reviews + report footer */}
            <EventCommunityFooter event={event} />
          </TabsContent>

          {/* Series History Tab */}
          <TabsContent value="series">
            {user ? (
              <SeriesHistoryPanel
                eventId={event.id}
                eventTitle={event.title}
                eventCity={event.city}
                eventState={event.state}
                onSeriesLinked={() => {
                  loadEvent();
                  loadSeriesHistory();
                }}
                fullWidth={true}
              />
            ) : (
              <div className="card-dashboard text-center py-12">
                <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">Track Event History</h3>
                <p className="text-gray-500 mb-4">Log in to track this event across multiple years and view your historical performance.</p>
                <Link to="/login">
                  <Button className="btn-primary">
                    Log In to Continue
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EventDetail;
